import { __ } from "@wordpress/i18n";
import { css } from "@/utils/css";
import { ADMIN_URL } from "@/utils/constants";
import forumax_logo from "@/assets/img/forumax.png";
import forumax_demo from "@/assets/img/convert-demo.png";
import eazydocs_logo from "@/assets/img/eazydocs.png";
import eazydocs_demo from "@/assets/img/docs-demo.png";

const PLUGIN_INFO: Record<
    string,
    {
        name: string;
        description: string;
        icon: React.ReactNode;
        feature_demo: React.ReactNode;
    }
> = {
    forumax: {
        name: __("Forumax", "antimanual"),
        description: __("Forumax is a forum plugin for WordPress that enables community discussions and support forums.", "antimanual"),
        icon: <img src={forumax_logo} alt={__("Forumax Logo", "antimanual")} />,
        feature_demo: <img src={forumax_demo} alt={__("Forumax Demo", "antimanual")} />,
    },
    eazydocs: {
        name: __("EazyDocs", "antimanual"),
        description: __("EazyDocs helps you build professional documentations in WordPress effortlessly with a simple user interface.", "antimanual"),
        icon: <img src={eazydocs_logo} alt={__("EazyDocs Logo", "antimanual")} />,
        feature_demo: <img src={eazydocs_demo} alt={__("EazyDocs Demo", "antimanual")} />,
    },
};

interface PluginRequiredProps {
    plugin: string;
    children?: React.ReactNode;
}

export function PluginRequired({ plugin, children }: PluginRequiredProps) {
    const pluginConfig = PLUGIN_INFO[plugin];

    if (!pluginConfig) {
        return null;
    }

    const { name, description, icon, feature_demo } = pluginConfig;

    const backendPluginInfo = (window.antimanual?.plugins as any)?.[plugin];

    const installed = backendPluginInfo?.is_installed ?? false;
    const activated = backendPluginInfo?.is_activated ?? false;

    const decodeUrl = (url: string) => {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = url;
        return textarea.value;
    };

    const activation_url = backendPluginInfo?.activation_url
        ? decodeUrl(backendPluginInfo.activation_url)
        : `${ADMIN_URL}/plugins.php?action=activate&plugin=${plugin}/${plugin}.php`;
    const installation_url = backendPluginInfo?.installation_url
        ? decodeUrl(backendPluginInfo.installation_url)
        : `${ADMIN_URL}/update.php?action=install-plugin&plugin=${plugin}`;
    const plugin_url = backendPluginInfo?.plugin_url ?? `https://wordpress.org/plugins/${plugin}/`;

    if (activated && children) {
        return <>{children}</>;
    }

    const href = installed ? activation_url : installation_url;
    const buttonText = installed ? __("Activate", "antimanual") : __("Install", "antimanual");
    const statusMessage = installed
        ? __("This plugin must be activated to use this feature.", "antimanual")
        : __("This plugin must be installed and activated to use this feature.", "antimanual");

    return (
        <div className={css("atml-plugin-required")}>
            {feature_demo}
            <div className={css("popup-container")}>
                <div className={css("popup-box")}>
                    <div className={css("info")}>
                        {icon}
                        <h2>
                            <a href={plugin_url} target="_blank" rel="noopener noreferrer">
                                {name}
                            </a>
                        </h2>
                        <p>
                            {description} {statusMessage}
                        </p>
                    </div>
                    <div className={css("bottom")}>
                        <p>
                            <strong>
                                <a href={plugin_url} target="_blank" rel="noopener noreferrer">
                                    {name}{" "}
                                </a>
                            </strong>
                        </p>
                        <div>
                            <a className={css("button button-primary")} href={href}>
                                {buttonText}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
